import { AmcLogo, SuccessLottieAnimation } from "web-core/components";
import {
  V2Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  VisuallyHidden,
  Textarea,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "web-design-system";
import {
  Client,
  Plan,
  PlansWithPlanType,
  PlanType,
  SIPPlan,
} from "creso-core/types";
import { format, parse } from "date-fns";
import {
  useEstimatedNextInstallmentDates,
  usePauseResumeTransaction,
} from "creso-core/hooks";
import { getErrorMessage } from "@/utils/error";
import { useEffect, useMemo, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { TRANSACTION_ORDER_TYPE_LABELS } from "creso-core/constants";
import { toast } from "sonner";
import { ChevronDownIcon } from "lucide-react";
import { getFirstName } from "creso-core/utils";

// Allow only alphanumeric input (a-z, A-Z, 0-9) plus whitespace.
// If the string fully matches this pattern, it is considered valid.
const TEXT_CHAR_REGEX = /^[a-zA-Z0-9\s]*$/;

type Props = PlansWithPlanType & {
  client: Client;
  open: boolean;
  toggle: (show: boolean) => void;
};

type PlanItemProps = {
  plan: Plan;
  planType: PlanType;
  estimatedNextInstallmentDate?: string | null;
};

function PlanName({ plan, planType }: PlanItemProps) {
  if (planType === "XSIP") {
    return (
      <span className="text-sm font-semibold">
        {(plan as SIPPlan).scheme_name}
      </span>
    );
  }
  return null;
}

function PlanStats({
  plan,
  planType,
  estimatedNextInstallmentDate,
}: PlanItemProps) {
  if (planType === "XSIP") {
    return (
      <>
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-xs text-muted-foreground">Amount</span>
          <span className="text-sm font-semibold">
            {plan.installment_amount}
          </span>
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <span className="text-xs text-muted-foreground">
            {plan.status === "PAUSED"
              ? "Estimated next installment"
              : "Next installment"}
          </span>
          <span className="text-sm font-semibold">
            {estimatedNextInstallmentDate
              ? format(
                  parse(
                    estimatedNextInstallmentDate as string,
                    "dd-MM-yyyy",
                    new Date()
                  ),
                  "PP"
                )
              : plan.installment_due_date
                ? format(plan.installment_due_date ?? "", "PP")
                : "-"}
          </span>
        </div>
      </>
    );
  }
  return null;
}

export function PauseResumeOrderDialog(props: Props) {
  const { mutateAsync: pauseResumeSip } = usePauseResumeTransaction();

  const { plans, planType, client } = props;

  const [reason, setReason] = useState<string>("");
  const [pendingPlans, setPendingPlans] = useState<Plan[]>(plans as Plan[]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  // The action (pause vs resume) is derived from the first selected plan and
  // applied to all plans in the list.
  const isPaused = plans[0]?.status === "PAUSED";

  useEffect(() => {
    if (props.open) {
      setPendingPlans(plans as Plan[]);
      setErrors({});
      setIsSuccess(false);
    }
    return () => {
      setReason("");
    };
  }, [props.open, plans]);

  // The estimated next installment date is only relevant when resuming
  // (currently paused) XSIP plans. We fetch it for every pending plan so the
  // correct date is shown per plan, not just for a single selection.
  const shouldFetchEstimatedDates =
    props.open && isPaused && planType === "XSIP";

  const estimatedDateItems = useMemo(() => {
    if (!shouldFetchEstimatedDates) return [];
    return (pendingPlans as SIPPlan[]).flatMap((plan) => {
      const prevInstallmentDate = plan.prev_paid_date || plan.start_date;
      if (!prevInstallmentDate) return [];
      return [
        {
          key: plan.id,
          payload: {
            prev_installment_date: format(
              new Date(prevInstallmentDate),
              "dd-MM-yyyy"
            ),
            frequency: plan.frequency,
            resumption: true,
          },
        },
      ];
    });
  }, [shouldFetchEstimatedDates, pendingPlans]);

  const { dates: estimatedDates } = useEstimatedNextInstallmentDates(
    estimatedDateItems,
    { enabled: shouldFetchEstimatedDates }
  );

  const router = useRouter();

  const handlePauseResume = async () => {
    if (reason && !TEXT_CHAR_REGEX.test(reason)) {
      toast.error("Reason can only contain text (no emojis or symbols).");
      return;
    }

    const payload = {
      pause_flag: !isPaused,
      pause_remarks: reason,
      investment_account_guid: client.investment_account ?? "",
    };

    const total = pendingPlans.length;

    const results = await Promise.allSettled(
      pendingPlans.map((plan) =>
        pauseResumeSip({
          xsipID: plan.id,
          payload,
        })
      )
    );

    const failedPlans: Plan[] = [];
    const nextErrors: Record<string, string> = {};
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const plan = pendingPlans[index];
        failedPlans.push(plan);
        nextErrors[plan.id.toString()] = getErrorMessage(
          result.reason,
          `Failed to ${isPaused ? "resume" : "pause"} SIP`
        );
      }
    });

    if (failedPlans.length === 0) {
      setIsSuccess(true);
      return;
    }

    // Stay on the dialog and keep only the plans that failed.
    setPendingPlans(failedPlans);
    setErrors(nextErrors);
    toast.error(
      `Failed to ${isPaused ? "resume" : "pause"} ${failedPlans.length} of ${total} ${TRANSACTION_ORDER_TYPE_LABELS[planType]}${total > 1 ? "s" : ""}`
    );
  };

  const openCart = () => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: "transaction",
        action: "view",
        client_id: client.id,
      }),
    });
    props.toggle(false);
  };

  return (
    <V2Dialog open={props.open} onOpenChange={props.toggle}>
      <DialogContent className="p-6 gap-6 space-y-0 w-120 max-w-none">
        {isSuccess ? (
          <>
            <VisuallyHidden>
              <DialogTitle>Added to cart</DialogTitle>
            </VisuallyHidden>
            <div className="flex flex-col items-center gap-2 py-4">
              <SuccessLottieAnimation loop={false} height={150} width={150} />
              <span className="text-lg font-semibold">Added to cart</span>
              <span className="text-sm text-muted-foreground text-center">
                {getFirstName(client.name)} needs to authorise from their device
              </span>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => props.toggle(false)}>
                Close
              </Button>
              <Button onClick={openCart}>Open Cart</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <VisuallyHidden>
                <DialogTitle className="text-lg">
                  Confirm {TRANSACTION_ORDER_TYPE_LABELS[planType]}{" "}
                  {isPaused ? "Resume" : "Pause"}?
                </DialogTitle>
              </VisuallyHidden>
              <div className="flex flex-col gap-4">
                <div className="text-lg font-semibold">
                  Confirm {TRANSACTION_ORDER_TYPE_LABELS[planType]}{" "}
                  {isPaused ? "Resume" : "Pause"}
                </div>
                <div className="bg-border h-px w-full" />
                <div className="flex flex-col gap-2 max-h-80 overflow-y-auto">
                  {pendingPlans.map((plan) => {
                    const error = errors[plan.id.toString()];
                    return (
                      <Collapsible
                        key={plan.id}
                        className="group/collapsible p-3 bg-muted flex flex-col rounded-lg"
                      >
                        <CollapsibleTrigger className="flex flex-row items-center gap-3 justify-between w-full text-left">
                          <div className="flex flex-row gap-3 items-center">
                            <AmcLogo
                              size="sm"
                              amc_code={plan.amc_morningstar_code}
                            />
                            <PlanName plan={plan} planType={planType} />
                          </div>
                          <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="flex flex-row w-full mt-5">
                            <PlanStats
                              plan={plan}
                              planType={planType}
                              estimatedNextInstallmentDate={
                                estimatedDates[plan.id] ?? null
                              }
                            />
                          </div>
                        </CollapsibleContent>
                        {error && (
                          <span className="text-xs text-destructive mt-2">
                            {error}
                          </span>
                        )}
                      </Collapsible>
                    );
                  })}
                </div>
                <Textarea
                  placeholder={`Enter reason for ${isPaused ? "resuming" : "pausing"} (optional)`}
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">
                  {isPaused
                    ? "Once confirmed, the SIP will resume from the next scheduled installment."
                    : "Once confirmed, the SIP will be paused for the next 2 installments and will resume after that."}
                </span>
              </div>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => props.toggle(false)}>
                No
              </Button>
              <Button onClick={handlePauseResume}>Proceed</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </V2Dialog>
  );
}
